import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowTrendingUpIcon,
  Bars3Icon,
  BuildingStorefrontIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  RocketLaunchIcon,
  ShoppingBagIcon,
  SparklesIcon,
  SquaresPlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import * as Avatar from '@radix-ui/react-avatar';
import clsx from 'clsx';
import { differenceInDays, endOfDay } from 'date-fns';
import React, { Fragment, useEffect, useRef } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { usePanel } from '@dashboard/context/PanelContext';
import useMatchMedia from '@dashboard/hooks/useMatchMedia';
import usePrevious from '@dashboard/hooks/usePrevious';
import { useUpdateNotification } from '@dashboard/hooks/useUpdateNotification';
import getSafeReadableColor from '@dashboard/scenes/PageEdit/utils/getSafeReadableColor';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import Button from '@dashboard/components/Button';
import Logo from '@dashboard/components/Logo';
import getSellerDomain from '@/dashboard/services/getSellerDomain';

const StyledNav = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  padding-top: ${p => (p.hasBanner ? '48px' : 0)};
  top: 0;
  bottom: 0;
  overflow-y: hidden;
  overflow-x: hidden;
  width: 260px;
  transition: left 200ms ease;
  z-index: 40;
  padding-bottom: 36px;

  @media (max-width: 768px) {
    padding-top: 32px;
    ${p => (p.isOpen ? `left: 0px; box-shadow: var(--shadow);` : `left: -250px`)};
  }
`;

const StyledNavContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${p => {
    if (p.hasUpgradeBanner) return '200px';
    if (p.hasNewUpdates) return '140px';
    return '100px';
  }};

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(#fff, 0.5);
    border: 3px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }
`;

const StyledNavFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding-top: 1rem;
`;

function NavList({ children }) {
  return <ul className="mb-4 px-4 mt-0 list-style-none">{children}</ul>;
}

function NavLinkText({ children, className }) {
  return <span className={clsx(`align-middle font-medium text-lg`, className)}>{children}</span>;
}

const NavLink = React.forwardRef(function NavLink(
  { children, className = '', isActive, ...props },
  ref,
) {
  return (
    <li>
      <RouterNavLink
        ref={ref}
        exact={true}
        className={clsx(
          `group flex w-full items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
          className,
          {
            'text-chocolate-600 bg-gray-50': isActive(),
            'text-gray-900 bg-transparent': !isActive(),
          },
        )}
        {...props}
      >
        {children}
      </RouterNavLink>
    </li>
  );
});

const NavAnchor = React.forwardRef(function NavAnchor(
  { children, className = '', isActive, ...props },
  ref,
) {
  return (
    <li>
      <a
        ref={ref}
        exact={true}
        className={clsx(
          `group flex w-full items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
          className,
        )}
        {...props}
      >
        {children}
      </a>
    </li>
  );
});

const PopoverLink = React.forwardRef(function PopoverLink(
  { children, className = '', ...props },
  ref,
) {
  return (
    <RouterLink
      ref={ref}
      className={clsx(
        `group flex w-full items-center space-x-2.5 rounded-md px-[18px] transition-all duration-300 hover:text-chocolate-600 py-1.5 text-gray-900 cursor-pointer`,
        className,
      )}
      {...props}
    >
      {children}
    </RouterLink>
  );
});

const PopoverAnchor = React.forwardRef(function PopoverAnchor(
  { children, className = '', ...props },
  ref,
) {
  return (
    <a
      ref={ref}
      target="_blank"
      className={clsx(
        `group flex w-full items-center space-x-2.5 rounded-md px-[18px] transition-all duration-300 hover:text-chocolate-600 py-1.5 text-gray-900`,
        className,
      )}
      {...props}
    >
      {children}
    </a>
  );
});

function OrdersDropdown({ pathname, isOpen, onToggle }) {
  return (
    <Disclosure as={Fragment}>
      {() => (
        <>
          <DisclosureButton
            onClick={onToggle}
            className={clsx(
              'group flex w-full items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2',
            )}
          >
            <ShoppingBagIcon className="h-5 w-5" />
            <NavLinkText>Orders</NavLinkText>
            <ChevronRightIcon
              className={clsx('h-4 w-4 ml-auto transition-transform duration-200 stroke-2', {
                'rotate-90': isOpen,
              })}
            />
          </DisclosureButton>

          {isOpen && (
            <DisclosurePanel static className="flex flex-col">
              <NavLink
                to="/orders/payments"
                isActive={() => pathname.startsWith('/orders/payments')}
                className="pl-10 !py-1"
              >
                {/* <BanknotesIcon className="h-5 w-5" /> */}
                <NavLinkText className="!text-[15px] font-normal">Payments</NavLinkText>
              </NavLink>

              <NavLink
                to="/orders/subscriptions"
                isActive={() => pathname.startsWith('/orders/subscriptions')}
                className="pl-10 !py-1"
              >
                {/* <ArrowPathRoundedSquareIcon className="h-5 w-5" /> */}
                <NavLinkText className="!text-[15px] font-normal">Subscriptions</NavLinkText>
              </NavLink>

              <NavLink
                to="/orders/bookings"
                isActive={() => pathname.startsWith('/orders/bookings')}
                className="pl-10 !py-1"
              >
                {/* <CalendarDaysIcon className="h-5 w-5" /> */}
                <NavLinkText className="!text-[15px] font-normal">Event bookings</NavLinkText>
              </NavLink>
            </DisclosurePanel>
          )}
        </>
      )}
    </Disclosure>
  );
}

function StoreSettingsDropdown({ pathname, seller, isOpen, onToggle }) {
  const settingsTabs = [
    { name: 'General', href: '/settings/general' },
    { name: 'Public details', href: '/settings/public-details' },
    { name: 'Team members', href: '/settings/members' },
    { name: 'Payments', href: '/settings/payments' },
    { name: 'Cart abandonment', href: '/settings/cart-abandonment' },
    { name: 'Invoices', href: '/settings/invoices' },
    { name: 'Domains', href: '/settings/domains' },
    { name: 'Email', href: '/settings/email' },
    { name: 'Customer portal', href: '/settings/customer-portal' },
    { name: 'Integrations', href: '/settings/integrations' },
    { name: 'Stripe Tax', href: '/settings/stripe-tax' },
    { name: 'Stripe & payouts', href: '/settings/stripe' },
    { name: 'API', href: '/settings/api' },
    { name: 'Webhooks', href: '/settings/webhooks' },
  ];

  return (
    <Disclosure as={Fragment}>
      {() => (
        <>
          <DisclosureButton
            onClick={onToggle}
            className={clsx(
              'group flex w-full items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2',
            )}
          >
            <Cog6ToothIcon className="h-5 w-5" />
            <NavLinkText>Store settings</NavLinkText>
            <ChevronRightIcon
              className={clsx('h-4 w-4 ml-auto transition-transform duration-200 stroke-2', {
                'rotate-90': isOpen,
              })}
            />
          </DisclosureButton>

          {isOpen && (
            <DisclosurePanel static className="flex flex-col">
              {settingsTabs.map(tab => (
                <NavLink
                  key={tab.href}
                  to={tab.href}
                  isActive={() => pathname === tab.href}
                  className="pl-10 !py-1"
                >
                  <NavLinkText className="!text-[15px] font-normal">{tab.name}</NavLinkText>
                </NavLink>
              ))}
            </DisclosurePanel>
          )}
        </>
      )}
    </Disclosure>
  );
}

function Navigation() {
  const isSmall = useMatchMedia('(max-width: 768px)');
  const [panelState, panelDispatch] = usePanel();
  const navNode = useRef(null);
  const menuButton = useRef(null);
  const { pathname } = useLocation();
  const previousPathname = usePrevious(pathname);
  const {
    user,
    seller,
    isAdminLoggedInAsSeller,
    isTrialing,
    isConnectedToStripe,
    hasActiveSubscription,
    isAuthenticating,
    isCanceled,
    isTrialExpired,
    plan,
    hasPastDueSubscription,
  } = useAuth();
  const { hasNewUpdates, markUpdatesAsSeen } = useUpdateNotification();
  const NavRef = useRef(null);

  // State to track which dropdown is open
  const [openDropdown, setOpenDropdown] = React.useState(() => {
    if (pathname.startsWith('/orders')) return 'orders';
    if (pathname.startsWith('/settings')) return 'settings';
    return null;
  });

  const isOpen = panelState.nav;
  const setIsOpen = value => panelDispatch({ type: 'set', panel: 'nav', value });

  // handle click outside NavRef
  useEffect(() => {
    function onClick(e) {
      if (
        NavRef &&
        NavRef.current &&
        menuButton &&
        menuButton.current &&
        !menuButton.current.contains(e.target)
      ) {
        if (!NavRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      }
    }

    window.addEventListener('click', onClick);

    return () => window.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (isSmall && previousPathname && previousPathname && pathname !== previousPathname) {
      setIsOpen(false);
    }
  }, [previousPathname, pathname]);

  useEffect(() => {
    function clickOutside(event) {
      if (
        isSmall &&
        navNode.current &&
        !navNode.current.contains(event.target) &&
        menuButton.current &&
        !menuButton.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    window.addEventListener('click', clickOutside, true);

    return () => window.removeEventListener('click', clickOutside, true);
  }, []);

  const trialDaysRemaining =
    seller.trialEnd && differenceInDays(endOfDay(new Date(seller.trialEnd)), new Date());

  // hide navigation if customer has no active subscription
  const shouldShowNavigation = !(
    (isAuthenticating === false &&
      hasActiveSubscription === false &&
      !isTrialing &&
      !isAdminLoggedInAsSeller) ||
    hasPastDueSubscription
  );

  const isPageDetail =
    pathname.includes('/pages/') &&
    !pathname.includes('/checkouts') &&
    !pathname.includes('/forms');

  const isUpgradePage = pathname.includes('/upgrade') || pathname.includes('/payment-due');

  const hasUpgradeBanner =
    !isUpgradePage &&
    ((isTrialing && seller.trialEnd) ||
      (seller._id && !hasActiveSubscription && !isTrialing));

  return (
    <>
      {isSmall && (
        <div className="block md:hidden absolute top-6 left-4 z-50">
          <Button
            appearance="light"
            onFocus={event => event.stopPropagation()}
            onClick={() => setIsOpen(!isOpen)}
            ref={menuButton}
          >
            <Bars3Icon className="w-5 h-5" />

            <div className="sr-only">Toggle Nav</div>
          </Button>
        </div>
      )}

      <StyledNav
        hasBanner={!isConnectedToStripe && !isPageDetail && user.onboardingStepsClosed}
        className={clsx('bg-white', {
          'border-r border-gray-100': isOpen || !isSmall,
        })}
        isOpen={isOpen}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        innerRef={NavRef}
      >
        <StyledNavContent hasUpgradeBanner={hasUpgradeBanner} hasNewUpdates={hasNewUpdates}>
          <div>
            <header className="flex flex-1 justify-between pt-6 pb-8">
              <nav style={{ width: '100%' }}>
                {!isSmall && (
                  <div className="pb-4 pl-4">
                    <RouterLink to="/" className="hover:text-chocolate-600 transition-colors">
                      <Logo size={40} />
                    </RouterLink>
                  </div>
                )}

                <NavList>
                  {shouldShowNavigation && (
                    <>
                      <NavLink to="/" isActive={() => pathname === '/'}>
                        <HomeIcon className="h-5 w-5" />
                        <NavLinkText>Dashboard</NavLinkText>
                      </NavLink>

                      <NavLink to="/pages" isActive={() => pathname.startsWith('/pages')}>
                        <SquaresPlusIcon className="h-5 w-5" />
                        <NavLinkText>Pages</NavLinkText>
                      </NavLink>

                      <OrdersDropdown
                        pathname={pathname}
                        isOpen={openDropdown === 'orders'}
                        onToggle={() =>
                          setOpenDropdown(current => (current === 'orders' ? null : 'orders'))
                        }
                      />

                      <NavLink
                        to="/submissions"
                        isActive={() => pathname.startsWith('/submissions')}
                      >
                        <DocumentTextIcon className="h-5 w-5" />
                        <NavLinkText>Submissions</NavLinkText>
                      </NavLink>

                      <NavLink to="/customers" isActive={() => pathname.startsWith('/customers')}>
                        <UserGroupIcon className="h-5 w-5" />
                        <NavLinkText>Customers</NavLinkText>
                      </NavLink>

                      <NavLink to="/coupons" isActive={() => pathname.startsWith('/coupons')}>
                        <ArrowTrendingUpIcon className="h-5 w-5" />
                        <NavLinkText>Coupons</NavLinkText>
                      </NavLink>
                    </>
                  )}

                  <StoreSettingsDropdown
                    pathname={pathname}
                    seller={seller}
                    isOpen={openDropdown === 'settings'}
                    onToggle={() =>
                      setOpenDropdown(current => (current === 'settings' ? null : 'settings'))
                    }
                  />

                  <NavAnchor href="/help" target="_blank">
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    <NavLinkText>Help center</NavLinkText>
                  </NavAnchor>
                </NavList>
              </nav>
            </header>
          </div>
        </StyledNavContent>

        <StyledNavFooter className="py-4">
          {!isUpgradePage && isTrialing && (
            <div className="m-4 border border-gray-100 rounded-md p-3">
              <h3>Grow</h3>
              <p>Free trial ends in {trialDaysRemaining} days</p>
              <Button
                href="/upgrade?upgradeFlow=sidebar"
                appearance="primary"
                className="mt-2 w-full justify-center"
              >
                <BoltIcon className="w-4 h-4 mr-1" /> Upgrade now
              </Button>
            </div>
          )}

          {!isUpgradePage && seller._id && !hasActiveSubscription && !isTrialing && (
            <div className="m-4 border border-gray-100 rounded-md p-3">
              <h3>{plan.title}</h3>
              {isCanceled && <p>Your subscription ended</p>}
              {isTrialExpired && <p>Your trial expired</p>}
              <Button href="/upgrade" appearance="primary" className="mt-2 w-full justify-center">
                <BoltIcon className="w-4 h-4 mr-1" /> Upgrade now
              </Button>
            </div>
          )}

          {(hasNewUpdates || (seller?._id && !hasActiveSubscription)) && (
            <NavList>
              <NavLink
                to="/updates"
                onClick={markUpdatesAsSeen}
                className="-mx-2"
                isActive={() => false}
              >
                <SparklesIcon className="h-5 w-5" />

                <NavLinkText className="!text-base">What&apos;s new</NavLinkText>
                <div className="absolute right-4 bg-chocolate-1000 rounded-full w-2 h-2" />
              </NavLink>
            </NavList>
          )}

          <Profile />
        </StyledNavFooter>
      </StyledNav>
    </>
  );
}

function Profile() {
  const { seller, logout, isAdmin, isAuthenticating } = useAuth();
  const { markUpdatesAsSeen } = useUpdateNotification();

  const width = 28;
  const height = 28;

  const logo = getImageSrcAndSrcSet({
    url: seller.logo?.url,
    width,
    height,
  });

  return (
    <Popover as="div" className="relative mx-4 flex-shrink-0">
      {() => (
        <>
          <>
            {!isAuthenticating && (
              <div>
                <PopoverButton className="flex rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-chocolate-500 focus:ring-offset-2 items-center gap-x-3 w-full hover:bg-gray-50 py-2 px-2">
                  <Avatar.Root className="h-7 w-7 flex-shrink-0">
                    {logo ? (
                      <Avatar.Image
                        src={logo.src}
                        srcSet={logo.srcSet}
                        alt={seller.name}
                        className="rounded-full"
                        width={width}
                        height={height}
                      />
                    ) : (
                      <Avatar.Fallback
                        className="w-full h-full flex rounded-full uppercase items-center justify-center overflow-hidden font-bold leading-loose"
                        delayMs={600}
                        style={{
                          background: seller.brandColor,
                          color: getSafeReadableColor(seller.brandColor),
                        }}
                      >
                        {seller.displayName?.charAt(0)}
                      </Avatar.Fallback>
                    )}
                  </Avatar.Root>

                  <div className="text-gray-900 flex-shrink font-medium overflow-hidden whitespace-nowrap w-full text-left">
                    {seller.displayName || seller.name || 'Untitled'}
                  </div>

                  <div className="">
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </div>
                </PopoverButton>
              </div>
            )}
          </>

          <Transition>
            <div className="relative">
              <PopoverPanel
                className={clsx(
                  'absolute bottom-14 z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-100 focus:outline-none py-1 text-gray-900',
                  'transition ease-out duration-100',
                  'data-[closed]:opacity-0 data-[closed]:scale-95',
                  'data-[open]:opacity-100 data-[open]:scale-100',
                )}
              >
                <PopoverButton as={PopoverLink} to="/account/profile">
                  <UserGroupIcon className="h-5 w-5" />
                  <span>Account</span>
                </PopoverButton>

                <PopoverButton as={PopoverLink} to="/billing/plan">
                  <CreditCardIcon className="h-5 w-5" />
                  <span>Plan & billing</span>
                </PopoverButton>

                <PopoverButton as={PopoverAnchor} href={`${getSellerDomain(seller)}/portal`}>
                  <BuildingStorefrontIcon className="h-5 w-5" />
                  <span>Customer portal</span>
                </PopoverButton>

                <PopoverButton as={PopoverLink} to="/updates" onClick={markUpdatesAsSeen}>
                  <SparklesIcon className="h-5 w-5" />
                  <span>What&apos;s new</span>
                </PopoverButton>

                <PopoverButton as={PopoverAnchor} href="/affiliate-program">
                  <RocketLaunchIcon className="h-5 w-5" />
                  <span>Affiliate program</span>
                </PopoverButton>

                {isAdmin && (
                  <PopoverButton as={PopoverLink} to="/admin">
                    <Cog8ToothIcon className="h-5 w-5" />
                    <span>Admin</span>
                  </PopoverButton>
                )}

                <PopoverButton onClick={logout} as={PopoverLink}>
                  <ArrowLeftEndOnRectangleIcon className="h-5 w-5" />
                  <span>Sign out</span>
                </PopoverButton>
              </PopoverPanel>
            </div>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default Navigation;
